import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { firstValueFrom } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { RrhhPermissionsService } from './rrhh-permissions.service';

/**
 * Servicio para verificar si un usuario tiene acceso al módulo RRHH.
 * Misma lógica que rrhhAccessGuard, reutilizable para login/logout y auditoría.
 */
@Injectable()
export class RrhhAccessService {
  private readonly afs = inject(AngularFirestore);
  private readonly afAuth = inject(AngularFireAuth);
  private readonly rrhhPermissionsService = inject(RrhhPermissionsService);

  /**
   * Verifica si el usuario tiene permisos de gestión elevados en RRHH.
   * No depende de nombres de rol; usa permisos detallados.
   * @param userId UID del usuario (Firebase Auth)
   * @returns true si posee permisos de gestión RRHH, false si no
   */
  async isAdmin(userId: string): Promise<boolean> {
    if (!userId) return false;

    try {
      const authUser = await this.afAuth.currentUser;
      if (!authUser || authUser.uid !== userId) return false;

      const [adminSectionAccess, managementAccess] = await Promise.all([
        firstValueFrom(this.rrhhPermissionsService.hasRrhhAdministrationSectionAccess().pipe(catchError(() => of(false)))),
        firstValueFrom(
          this.rrhhPermissionsService
            .canAccessAnyOf([
              'vacaciones.aprobar',
              'vacaciones.editar',
              'vacaciones.eliminar',
              'solicitudes.aprobar',
              'solicitudes.denegar',
              'solicitudes.cambiar',
            ])
            .pipe(catchError(() => of(false)))
        ),
      ]);

      return adminSectionAccess || managementAccess;
    } catch {
      return false;
    }
  }

  /**
   * Verifica si el usuario tiene acceso a RRHH.
   * @param userId UID del usuario (Firebase Auth)
   * @returns true si tiene acceso, false si no
   */
  async hasRrhhAccess(userId: string): Promise<boolean> {
    if (!userId) return false;

    try {
      const authUser = await this.afAuth.currentUser;
      if (!authUser || authUser.uid !== userId) return false;

      const userDoc = await firstValueFrom(this.afs.doc(`users/${userId}`).get().pipe(take(1)));
      const user = userDoc?.exists ? (userDoc.data() as { internal?: boolean }) : null;

      if (!user) return false;
      if (user.internal === false) return false;

      return await firstValueFrom(this.rrhhPermissionsService.hasRrhhModuleAccess().pipe(catchError(() => of(false))));
    } catch {
      return false;
    }
  }
}
